import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Table, TableBody, TableCell, TableHead,
  TableRow, IconButton, Paper, Tabs, Tab, FormControl, InputLabel,
  Select, MenuItem, SelectChangeEvent
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Project } from '../../types/projectTypes';
import { PbiLink } from '../../types/pbiLinkTypes';
import { getActiveProjects } from '../../api/projectApi';
import { getUserProject } from '../../api/userApi';
import { getPbiLinksByProjectId, createPbiLink, updatePbiLink, deletePbiLink } from '../../api/pbiLinkApi';
import { profiles, ADMIN_SISTEMA } from '../../contexts/constants/profiles';

const PowerBiReports: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<number>();
  const [items, setItems] = useState<PbiLink[]>([]);
  const [selected, setSelected] = useState<PbiLink | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<PbiLink | null>(null);
  const [form, setForm] = useState({ title: '', url: '' });

  useEffect(() => { fetchProjects(); }, []);

  useEffect(() => {
    if (selectedProject) fetchLinks();
    else { setItems([]); setSelected(null); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const userStr = localStorage.getItem("user");
      let role = "", rut = "";
      if (userStr) {
        const user = JSON.parse(userStr);
        role = user.rols?.description;
        rut = user.identificationNum;
      }
      let projectsData: Project[] = [];
      if (role === profiles[0].name || role === profiles[2].name || role === profiles[3].name || role === ADMIN_SISTEMA) {
        projectsData = await getActiveProjects();
      } else {
        const userProjects = await getUserProject(rut);
        projectsData = Array.isArray(userProjects) ? userProjects : [];
      }
      setProjects(projectsData.filter((p) => p.status === undefined || p.status === true));
    } catch (error) { console.error(error); setProjects([]); }
  };

  const fetchLinks = async () => {
    if (!selectedProject) return;
    try {
      const data = await getPbiLinksByProjectId(selectedProject);
      setItems(data);
      setSelected(data[0] ?? null);
    } catch (error) { console.error(error); setItems([]); setSelected(null); }
  };

  const handleProjectChange = (e: SelectChangeEvent<number>) => {
    setSelectedProject(Number(e.target.value));
  };

  const openModal = () => {
    setEditItem(null);
    setForm({ title: '', url: '' });
    setModalOpen(true);
  };

  const openEdit = (item: PbiLink) => {
    setEditItem(item);
    setForm({ title: item.title, url: item.url });
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.url.trim() || !selectedProject) return;
    try {
      if (editItem && editItem.id) {
        const updated = await updatePbiLink(editItem.id, { ...editItem, ...form });
        setItems(prev => prev.map(i => i.id === editItem.id ? updated : i));
        if (selected?.id === editItem.id) setSelected(updated);
      } else {
        const created = await createPbiLink({ projectId: selectedProject, title: form.title, url: form.url });
        setItems(prev => [...prev, created]);
        if (!selected) setSelected(created);
      }
      setEditItem(null);
      setForm({ title: '', url: '' });
    } catch (error) { console.error(error); }
  };

  const handleDelete = async (id: number) => {
    try {
      await deletePbiLink(id);
      setItems(prev => {
        const updated = prev.filter(i => i.id !== id);
        if (selected?.id === id) setSelected(updated[0] ?? null);
        return updated;
      });
    } catch (error) { console.error(error); }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditItem(null);
    setForm({ title: '', url: '' });
  };

  const getEmbedUrl = (link: string) => {
    try {
      return link.replace('/view?', '/reportEmbed?');
    } catch { return link; }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">Informes Power BI</Typography>
        {selectedProject && <Button variant="contained" onClick={openModal}>Agregar PowerBI</Button>}
      </Box>

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Proyecto</InputLabel>
        <Select value={selectedProject || ''} label="Proyecto" onChange={handleProjectChange}>
          {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </Select>
      </FormControl>

      {items.length > 0 && selected && (
        <Box>
          <Tabs
            value={selected.id}
            onChange={(_, val) => setSelected(items.find(i => i.id === val) ?? null)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            {items.map(item => (
              <Tab key={item.id} label={item.title} value={item.id} />
            ))}
          </Tabs>
          <Paper elevation={2} sx={{ width: '100%', height: '600px' }}>
            <iframe
              title={selected.title}
              src={getEmbedUrl(selected.url)}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              allowFullScreen
            />
          </Paper>
        </Box>
      )}

      <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth="md">
        <DialogTitle>Gestionar PowerBI</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Título" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} fullWidth />
          <TextField label="Link del PowerBI" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} fullWidth />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {editItem && <Button onClick={() => { setEditItem(null); setForm({ title: '', url: '' }); }}>Cancelar edición</Button>}
            <Button variant="contained" onClick={handleSave}>{editItem ? 'Actualizar' : 'Agregar'}</Button>
          </Box>

          {items.length > 0 && (
            <Paper>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell><strong>Título</strong></TableCell>
                    <TableCell><strong>Link</strong></TableCell>
                    <TableCell align="center"><strong>Acciones</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map(item => (
                    <TableRow key={item.id} hover selected={editItem?.id === item.id}>
                      <TableCell>{item.title}</TableCell>
                      <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.url}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => openEdit(item)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => item.id && handleDelete(item.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PowerBiReports;
